import { ChangeEvent, KeyboardEvent, useId } from 'react';

type Props = {
    label: string;
    suffix: string;
    min: number;
    max: number;
    value: number | null;
    onChange: (value: number | null) => void;
    formatNumber: (number: number) => string;
    rangeDescription: string;
};

export const SliderInput = ({ label, suffix, value, onChange, min, max, formatNumber, rangeDescription }: Props) => {
    const defaultStep = 1;
    const inputId = useId();
    const labelId = useId();
    const suffixId = useId();
    const rangeId = useId();

    const normalizeValue = ({ target }: ChangeEvent<HTMLInputElement>) => {
        const raw = target?.value?.replace(/\s/g, '') || '';

        const num = parseInt(raw, 10);
        if (!isNaN(num)) {
            return num;
        }

        return null;
    };

    const updateValue = (event: ChangeEvent<HTMLInputElement>) => {
        onChange(normalizeValue(event));
    };

    const onBlur = (event: ChangeEvent<HTMLInputElement>) => {
        const value = normalizeValue(event);
        if (!value) {
            return onChange(min);
        }
        if (value < min) {
            return onChange(min);
        }
        if (value > max) {
            return onChange(max);
        }
    };

    const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        const step = e.shiftKey ? 10 * defaultStep : 1 * defaultStep;
        if (e.key === 'ArrowUp') {
            e.preventDefault();
            const newValue = Math.min((value || 0) + step, max);
            onChange(newValue);
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            const newValue = Math.max((value || 0) - step, min);
            onChange(newValue);
        }
    };

    return (
        <div className="r-mb-4 r-flex r-items-center r-justify-between md:r-items-end">
            <div className="r-flex r-flex-col r-gap-2">
                <div className="r-flex r-flex-row r-gap-2">
                    <div className="r-size-6 r-rounded-full r-bg-primary-200">{/* temporary icon */}</div>
                    <label id={labelId} htmlFor={inputId} className="r-text-neutral-900">
                        {label}
                    </label>
                </div>
                <p className="r-text-xl r-font-bold" aria-hidden="true">
                    {formatNumber(value || 0)} {suffix}
                </p>
            </div>
            <div className="r-relative">
                <input
                    id={inputId}
                    type="text"
                    inputMode="numeric"
                    aria-labelledby={`${labelId} ${suffixId}`}
                    aria-describedby={rangeId}
                    onBlur={onBlur}
                    onKeyDown={onKeyDown}
                    onChange={updateValue}
                    value={formatNumber(value || 0)}
                    className="r-h-[56px] r-w-[128px] r-rounded-2xl r-border r-border-neutral-400 r-pr-8 r-text-end r-text-lg"
                />
                <span id={suffixId} className="r-absolute r-right-3 r-top-1/2 r--translate-y-1/2">
                    {suffix}
                </span>
                <span id={rangeId} className="r-sr-only">
                    {rangeDescription}
                </span>
            </div>
        </div>
    );
};
